import React from 'react';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { SalonDashboardReport, reportApi } from '../../../services/reportService';
import { StatTile } from '../../../components/ui';
import { countLabel } from '../../../utils/format';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  CHART_COLORS,
  formatPLN,
  formatNumber,
  useAsyncData,
  pickGranularity,
  EmptyHint,
  daysBetween,
  StatTileGrid,
  StatsRow,
} from './statsShared';
import { RevenueAreaChart, DonutChart } from './ChartKit';

interface Props {
  salonId: string;
  range: DateRange;
  data: SalonDashboardReport;
}

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

export const RevenueTab: React.FC<Props> = ({ salonId, range, data }) => {
  const granularity = pickGranularity(range.dateFrom, range.dateTo);
  const { data: ts } = useAsyncData(
    () => reportApi.getTimeseries(salonId, range.dateFrom, range.dateTo, granularity),
    [salonId, range.dateFrom, range.dateTo, granularity],
  );

  const days = daysBetween(range.dateFrom, range.dateTo);
  const avgPerVisit = data.bookings.completed > 0 ? data.revenue.total / data.bookings.completed : 0;
  const avgPerDay = data.revenue.total / days;

  const pay = data.paymentBreakdown;
  const payData = [
    { name: 'Karta', value: pay.card, color: CHART_COLORS[0] },
    { name: 'Gotówka', value: pay.cash, color: CHART_COLORS[1] },
    { name: 'BLIK', value: pay.blik, color: CHART_COLORS[2] },
    { name: 'Przelew', value: pay.transfer, color: CHART_COLORS[3] },
  ];

  return (
    <Stack gap={2}>
      <StatTileGrid columns={3} sx={{ '& > :first-of-type': { gridColumn: { xs: '1 / -1', md: 'auto' } } }}>
        <StatTile label="Przychód razem" value={formatPLN(data.revenue.total)} change={Math.round(data.revenue.change)} changeLabel="niż poprzednio" />
        <StatTile label="Średnio na wizytę" value={formatPLN(avgPerVisit)} hint={`Z ${countLabel(data.bookings.completed, 'zakończonej wizyty', 'zakończonych wizyt', 'zakończonych wizyt')}`} />
        <StatTile label="Średnio dziennie" value={formatPLN(avgPerDay)} hint={`W ciągu ${countLabel(days, 'dnia', 'dni', 'dni')}`} />
      </StatTileGrid>

      <StatsRow template="2fr 1fr">
        <SectionCard title="Przychód w czasie" subtitle={granularity === 'week' ? 'Sumy tygodniowe' : 'Sumy dzienne'}>
          {ts && ts.buckets.some((b) => b.revenue > 0) ? (
            <RevenueAreaChart data={ts.buckets.map((b) => ({ label: b.label, revenue: b.revenue }))} height={320} />
          ) : (
            <EmptyHint description="Wybierz dłuższy okres albo inny zakres dat.">Brak przychodu w wybranym okresie</EmptyHint>
          )}
        </SectionCard>
        <SectionCard title="Według metody płatności">
          {payData.some((p) => p.value > 0) ? <DonutChart data={payData} /> : <EmptyHint>Brak wpłat w wybranym okresie</EmptyHint>}
        </SectionCard>
      </StatsRow>

      <SectionCard title="Przychód według usługi" padding={data.topServices.length ? 'none' : 'normal'}>
        {data.topServices.length ? (
          <TableContainer>
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Usługa</TableCell>
                  <TableCell align="right">Liczba</TableCell>
                  <TableCell align="right">Średnia cena</TableCell>
                  <TableCell align="right">Przychód</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.topServices.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                    <TableCell align="right" sx={NUM}>{formatNumber(s.count)}</TableCell>
                    <TableCell align="right" sx={{ ...NUM, whiteSpace: 'nowrap' }}>{formatPLN(s.avgPrice)}</TableCell>
                    <TableCell align="right" sx={{ ...NUM, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatPLN(s.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyHint>Brak usług w wybranym okresie</EmptyHint>
        )}
      </SectionCard>
    </Stack>
  );
};
